import { hasCSSSelectorSupport } from "@otto-ec/otto-components-utils/utils/canIUse";
import { mount, unmount } from "svelte";
import { extendElementWithActionAttributes } from "../customElement/extendElementWithActionAttributes.ts";
import type { CustomElement } from "../customElement/utils.ts";
import { useShadowStylesInjector } from "./utils/useShadowStylesInjector.ts";
import { setHost } from "./utils/useHost.ts";
import { createObservedAttributes } from "./utils/createObservedAttributes.ts";
import type { DefineComponentOptions } from "./types.ts";
import { setMounted } from "./utils/setMounted.ts";

/**
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 */
export function defineComponent({
  tag,
  styles,
  props,
  html,
  extendOptions = {},
  component,
}: DefineComponentOptions): void {
  const injectShadowStyles = useShadowStylesInjector(styles);

  const attributeNameToPropertyNameMap = Object.fromEntries(
    Object.entries(props).map(
      ([propName, { attribute = propName }]) => [attribute as string, propName] as const,
    ),
  );

  const createInitialState = (): Record<string, unknown> =>
    Object.fromEntries(
      Object.entries(props).map(
        ([propName, { type }]) => [propName, type === "Boolean" ? false : undefined] as const,
      ),
    );

  /**
 *
 *
 */
  const reflecting = new WeakSet<HTMLElement>();

  let ElementClass = class extends HTMLElement {
    static observedAttributes = createObservedAttributes(props);

    mounted: boolean = false;
    $ocState: Record<string, unknown>;
    #ocEffectRootUnset?: () => void;
    #svelteComponent?: Record<string, unknown>;

    constructor() {
      super();

      this.$ocState = $state(createInitialState());
    }

    attributeChangedCallback(name: string, _: string, newValue: string): void {
      if (reflecting.has(this)) return;
      const key = attributeNameToPropertyNameMap[name];
      this.$ocState[key] = newValue;
    }

    async connectedCallback(): Promise<void> {
      this.#ocEffectRootUnset = $effect.root(() => {
        Object.entries(props).forEach(([propName, { type, attribute = propName, reflect }]) => {
          if (!reflect) return;

          $effect(() => {
            const value = this.$ocState[propName];
            reflecting.add(this);
            if (type === "Boolean") {
              this.toggleAttribute(attribute, Boolean(value));
            } else if (value == null) {
              this.removeAttribute(attribute);
            } else {
              this.setAttribute(attribute, String(value));
            }
            reflecting.delete(this);
          });
        });
      });

      if (html) (this.shadowRoot ? this.shadowRoot : this).innerHTML = html;

      if (component) {
        setHost(this);
        this.#svelteComponent = mount(component, {
          target: this.shadowRoot ? this.shadowRoot : this,
          props: this.$ocState,
        });
        /*                                          */
        Object.assign(this, this.#svelteComponent);
        setHost(undefined);
      }

      await injectShadowStyles(this);

      setMounted(tag, this, true);
    }

    async disconnectedCallback(): Promise<void> {
      this.#ocEffectRootUnset?.();
      if (this.#svelteComponent) await unmount(this.#svelteComponent);

      setMounted(tag, this, false);
    }
  } as unknown as CustomElement;

  Object.entries(props).forEach(([propName, { type, attribute = propName, reflect }]) => {
    Object.defineProperty(ElementClass.prototype, propName, {
      get(this): unknown {
        return this.$ocState[propName];
      },
      set(this, value: unknown) {
        this.$ocState[propName] = value;
        /*                                                                   */
        /*                                                                   */
        /*                                                                  */
        /*                                                                   */
        /*                                                                    */
        if (reflect) {
          reflecting.add(this);
          if (type === "Boolean") {
            this.toggleAttribute(attribute, Boolean(value));
          } else if (value == null) {
            this.removeAttribute(attribute);
          } else {
            this.setAttribute(attribute, String(value));
          }
          reflecting.delete(this);
        }
      },
    });
  });

  ElementClass = window.__components.extend(extendOptions)(ElementClass);

  /*                                             */
  /*                                                    */
  if (!hasCSSSelectorSupport) ElementClass = extendElementWithActionAttributes(ElementClass);

  customElements.define(tag, ElementClass);
}

window.__components ??= {} as never;
window.__components.defineComponent = defineComponent;
